import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../config/api";
import { useLocation } from "../contexts/LocationContext";
import { useBooking } from "../contexts/BookingContext";
import AgeWarningModal from "../components/AgeWarningModal";
import AmenityList, { parseAmenities } from "../components/AmenityList";
import NearbyLocationsList from "../components/NearbyLocationsList";
import { enrichScheduleMovies } from "../utils/scheduleEnrichment";
import "./ShowTimepage.css";

// ─── Helpers (same as CinemaPage) ──────────────────────────────────────────

const formatDurationLabel = (value) => {
  const mins = Number(value);
  if (Number.isFinite(mins) && mins > 0) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  }
  if (typeof value === "string" && value.trim().length > 0) return value;
  return "Duration TBA";
};

const parseAttributes = (attrString) => {
  const result = {};

  attrString
    .split("|")
    .filter(Boolean)
    .forEach((item) => {
      const [key, value] = item.split("=");
      if (key && value) {
        result[key.toLowerCase()] = value;
      }
    });

  return result;
};

const formatShowtimeLabel = (showtime) => {
  const hasAMPM = (s) => {
    if (!s) return false;
    const u = s.toUpperCase().trim();
    return (
      u.endsWith("AM") ||
      u.endsWith("PM") ||
      u.includes(" AM") ||
      u.includes(" PM")
    );
  };
  const convertTo12Hour = (s) => {
    const m = s.match(/(\d{1,2}):(\d{2})/);
    if (m) {
      let h = parseInt(m[1], 10);
      const min = parseInt(m[2], 10);
      const ampm = h >= 12 ? "PM" : "AM";
      h = h % 12 || 12;
      return `${h}:${min.toString().padStart(2, "0")} ${ampm}`;
    }
    return s;
  };

  if (showtime.label)
    return hasAMPM(showtime.label)
      ? showtime.label
      : convertTo12Hour(showtime.label);
  if (showtime.time && showtime.time !== "TBA")
    return hasAMPM(showtime.time)
      ? showtime.time
      : convertTo12Hour(showtime.time);
  if (showtime.startDateTime) {
    const d = new Date(showtime.startDateTime);
    if (!isNaN(d.getTime())) {
      let h = d.getHours();
      const min = d.getMinutes();
      const ampm = h >= 12 ? "PM" : "AM";
      h = h % 12 || 12;
      return `${h}:${min.toString().padStart(2, "0")} ${ampm}`;
    }
  }
  return "Time TBA";
};

const parseCastArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value))
    return value.filter((e) => typeof e === "string" && e.trim());
  if (typeof value === "string")
    return value
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);
  return [];
};

// ─── CinemaAccordion (inline schedule per cinema) ──────────────────────────

const CinemaAccordion = ({ cinema, onClose }) => {
  const navigate = useNavigate();
  const {
    setTheater,
    setQuantity,
    quantity,
    setVistaTransactionId,
    setSelectedSeats,
    setMovie,
    setShowtime,
  } = useBooking();

  useEffect(() => {
    setQuantity(null); // ✅ reset seat count when opening showtimes
  }, []);

  const [movies, setMovies] = useState([]);
  const [fullCinema, setFullCinema] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [scheduleCache, setScheduleCache] = useState({}); // enriched cinema from schedule (has vistaCinemaId)
  const [selectedDate, setSelectedDate] = useState(null);
  const [expandedMovies, setExpandedMovies] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isInitializingBooking, setIsInitializingBooking] = useState(false);
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [pendingShowtime, setPendingShowtime] = useState(null);
  const [pendingMovie, setPendingMovie] = useState(null);
  const [showAgeWarning, setShowAgeWarning] = useState(false);
  const [pendingBookingData, setPendingBookingData] = useState(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const showError = (msg) => {
    setErrorMessage(msg);
    setShowErrorPopup(true);
  };

  //   const availableDates = useMemo(() => {
  //     const dates = [];
  //     const today = new Date();
  //     for (let i = 0; i < 5; i++) {
  //       const d = new Date(today);
  //       d.setDate(today.getDate() + i);
  //       const dateStr = d.toISOString().split("T")[0];
  //       dates.push({
  //         date: d,
  //         dateStr,
  //         label: i === 0 ? "Today" : i === 1 ? "Tomorrow" : d.toLocaleDateString("en-US", { weekday: "short" }),
  //       });
  //     }
  //     return dates;
  //   }, []);

  //   const fetchSchedule = useCallback(async (dateStr) => {
  //     if (!cinema?.id) return;
  //     try {
  //       setLoading(true);
  //       setError("");
  //       const schedule = await api.getCinemaSchedule(cinema.id, dateStr);
  //       if (!schedule || !schedule.cinema) {
  //         setMovies([]);
  //         setError("No schedule found.");
  //         return;
  //       }
  //       const enrichedCinema = { ...schedule.cinema, amenities: parseAmenities(schedule.cinema.amenities) };
  //       setFullCinema(enrichedCinema);
  //       setTheater(enrichedCinema);
  //       setMovies(Array.isArray(schedule.movies) ? schedule.movies : []);
  //     } catch (e) {
  //       console.error(e);
  //       setMovies([]);
  //       setError("Failed to load schedule.");
  //     } finally {
  //       setLoading(false);
  //     }
  //   }, [cinema?.id, setTheater]);

  //   useEffect(() => { fetchSchedule(selectedDate); }, [fetchSchedule, selectedDate]);

  useEffect(() => {
    if (!cinema?.id) return;
    const discover = async () => {
      setLoading(true);
      setError("");

      const candidates = Array.from({ length: 14 }, (_, offset) => {
        const d = new Date();
        d.setDate(d.getDate() + offset);
        const dateStr = d.toLocaleDateString("en-CA");
        const label = d.toLocaleDateString("en-US", { weekday: "short" });
        return { date: d, dateStr, label };
      });

      const results = await Promise.all(
        candidates.map(async (candidate) => {
          try {
            const schedule = await api.getCinemaSchedule(
              cinema.id,
              candidate.dateStr,
            );
            const moviesForDate = Array.isArray(schedule?.movies)
              ? schedule.movies
              : [];
            const hasShows = moviesForDate.some(
              (m) => Array.isArray(m.showtimes) && m.showtimes.length > 0,
            );
            return { candidate, schedule, movies: moviesForDate, hasShows };
          } catch {
            return { candidate, schedule: null, movies: [], hasShows: false };
          }
        }),
      );

      const validResults = results.filter((r) => r.hasShows);

      if (validResults.length === 0) {
        setAvailableDates([]);
        setMovies([]);
        setError("No shows available in the next 5 days.");
        setLoading(false);
        return;
      }

      let dbMovies = [];
      try {
        dbMovies = await api.getMovies({ limit: 100 });
      } catch (e) {
        console.warn("Could not fetch DB movies:", e);
      }
      const isValid = (val) => {
        if (!val) return false;
        const v = String(val).toLowerCase().trim();
        return v !== "duration tba" && v !== "tba" && v !== "nr" && v !== "na";
      };

      const enrichWithPosters = (scheduleMovies) =>
        enrichScheduleMovies(scheduleMovies, dbMovies);
      const cache = {};
      validResults.forEach((r) => {
        cache[r.candidate.dateStr] = {
          ...r,
          movies: enrichWithPosters(r.movies),
        };
      });
      setScheduleCache(cache);
      setAvailableDates(validResults.map((r) => r.candidate));

      const first = validResults[0];
      setSelectedDate(first.candidate.dateStr);
      setMovies(enrichWithPosters(first.movies));
      if (first.schedule?.cinema) {
        const enriched = {
          ...first.schedule.cinema,
          amenities: parseAmenities(first.schedule.cinema.amenities),
        };
        setFullCinema(enriched);
        setTheater(enriched);
      }
      setLoading(false);
    };
    discover();
  }, [cinema?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDateChange = (dateStr) => {
    setSelectedDate(dateStr);
    setExpandedMovies(new Set());
    const cached = scheduleCache[dateStr];
    if (cached) {
      setMovies(cached.movies);
      if (cached.schedule?.cinema && !fullCinema) {
        const enriched = {
          ...cached.schedule.cinema,
          amenities: parseAmenities(cached.schedule.cinema.amenities),
        };
        setFullCinema(enriched);
        setTheater(enriched);
      }
    }
  };

  const filteredMovies = useMemo(
    () =>
      movies.filter(
        (m) => Array.isArray(m.showtimes) && m.showtimes.length > 0,
      ),
    [movies],
  );

  const getShowtimeStatus = useCallback((showtime) => {
    const now = new Date();
    const showDateTime = showtime.startDateTime
      ? new Date(showtime.startDateTime)
      : new Date(`${showtime.date} ${showtime.time}`);
    if (!isNaN(showDateTime.getTime()) && showDateTime < now) return "lapsed";
    // const seed = (
    //   showtime.id || `${showtime.movieId}-${showtime.label || showtime.time}`
    // )
    //   .split("")
    //   .reduce((acc, c) => {
    //     acc = (acc << 5) - acc + c.charCodeAt(0);
    //     return acc & acc;
    //   }, 0);
    // const pct = Math.abs(seed) % 100;
    // if (pct >= 100) return "sold-out";
    // if (pct >= 70) return "filling-fast";
    // Added new logic to calculate filling fast
    let totalSeats = parseInt(showtime.totalSeats);
    let availableSeats = parseInt(showtime.availableSeats);
    let soldSeats = totalSeats - availableSeats;
    let occupiedPercent =
      totalSeats > 0
        ? Math.max(0, Math.min(100, Math.round((soldSeats / totalSeats) * 100)))
        : 0;
    if (availableSeats === 0) return "sold-out";
    if (occupiedPercent >= 55) return "filling-fast";

    return "available";
  }, []);

  const proceedWithBooking = async (movie, showtime) => {
    // Use fullCinema (fetched from schedule) — it contains the real vistaCinemaId
    const activeCinema = fullCinema || cinema;
    setSelectedSeats([]);
    setVistaTransactionId(null);
    try {
      setIsInitializingBooking(true);

      // vistaCinemaId from the schedule response — NOT the listing cinema prop
      const vistaCinemaId =
        activeCinema.vistaCinemaId ||
        activeCinema.vistacinemaid ||
        activeCinema.vista_cinema_id;

      if (!vistaCinemaId) {
        showError("Vista Cinema ID not found. Please contact support.");
        console.error("Missing vistaCinemaId. Cinema object:", activeCinema);
        return;
      }

      const sessionId =
        showtime.id || showtime.sessionId || showtime.vistaShowId;
      if (!sessionId) {
        showError("Unable to initialize booking. Please try again.");
        return;
      }

      // console.log("Initializing Vista booking with:", {
      //   vistaCinemaId,
      //   sessionId,
      // });
      const bookingData = await api.initVistaBooking(vistaCinemaId, sessionId);
      if (bookingData?.transactionId) {
        setVistaTransactionId({
          transactionId: bookingData.transactionId,
          sessionId,
        });
        setMovie(movie);
        setTheater(activeCinema);
        const completeShowtime = {
          ...showtime,
          id: sessionId,
          sessionId,
          vistaShowId: sessionId,
          date: selectedDate,
        };
        setShowtime(completeShowtime);
        navigate("/seat-selection", {
          state: {
            movie,
            showtime: completeShowtime,
            theater: activeCinema,
            selectedDate,
            vistaTransactionId: bookingData.transactionId,
            vistaSessionId: sessionId,
          },
        });
      } else {
        throw new Error("Failed to get transaction ID");
      }
    } catch (err) {
      showError(
        `Failed to initialize booking: ${err.message || "Please try again."}`,
      );
    } finally {
      setIsInitializingBooking(false);
    }
  };

  const handleShowtimeSelect = async (movie, showtime) => {
    if (!fullCinema && !cinema) return;
    const activeCinema = fullCinema || cinema;
    const ageRating = movie.ageRating || movie.rating || "";
    if (ageRating.toUpperCase() === "A") {
      setPendingBookingData({ movie, showtime });
      setShowAgeWarning(true);
      return;
    }
    if (!quantity || quantity < 1 || quantity > 10) {
      setPendingShowtime(showtime);
      setPendingMovie(movie);
      setShowQuantityModal(true);
      return;
    }
    await proceedWithBooking(movie, showtime);
  };

  const handleQuantitySelect = () => {
    if (quantity && quantity >= 1 && quantity <= 10) {
      setShowQuantityModal(false);
      if (pendingShowtime && pendingMovie)
        proceedWithBooking(pendingMovie, pendingShowtime);
      setPendingShowtime(null);
      setPendingMovie(null);
    }
  };

  const handleAgeWarningConfirm = async () => {
    setShowAgeWarning(false);
    if (pendingBookingData) {
      const { movie, showtime } = pendingBookingData;
      setPendingBookingData(null);
      if (!quantity || quantity < 1 || quantity > 10) {
        setPendingShowtime(showtime);
        setPendingMovie(movie);
        setShowQuantityModal(true);
      } else await proceedWithBooking(movie, showtime);
    }
  };

  return (
    <div className="cinema-accordion-body">
      {/* Date Picker + Controls */}
      <div className="acc-date-bar">
        <div className="acc-date-picker">
          {availableDates.map((di, i) => (
            <div
              key={i}
              className={`acc-date-item ${selectedDate === di.dateStr ? "active" : ""}`}
              onClick={() => handleDateChange(di.dateStr)}
            >
              <span className="acc-month">
                {di.date
                  .toLocaleDateString("en-US", { month: "short" })
                  .toUpperCase()}
              </span>
              <span className="acc-day">
                {di.date.getDate().toString().padStart(2, "0")}
              </span>
              <span className="acc-weekday">{di.label.toUpperCase()}</span>
            </div>
          ))}
        </div>

        <div className="acc-controls">
          <div className="acc-legend">
            {[
              ["available", "Available"],
              ["filling-fast", "Filling Fast"],
              ["sold-out", "Sold Out"],
              ["lapsed", "Lapsed"],
            ].map(([cls, label]) => (
              <span key={cls} className="acc-legend-item">
                <span className={`acc-legend-dot ${cls}`}></span>
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Movies */}
      <div className="acc-movies">
        {loading ? (
          <div className="acc-loading">
            {[1, 2].map((i) => (
              <div key={i} className="acc-skeleton">
                <div className="sk-poster"></div>
                <div className="sk-content">
                  <div className="sk-title"></div>
                  <div className="sk-tags">
                    <div></div>
                    <div></div>
                    <div></div>
                  </div>
                  <div className="sk-times">
                    <div></div>
                    <div></div>
                    <div></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="acc-empty">
            <p>{error}</p>
          </div>
        ) : filteredMovies.length === 0 ? (
          <div className="acc-empty">
            <p>No movies scheduled for this date.</p>
          </div>
        ) : (
          filteredMovies.map((movie) => {
            const isExpanded = expandedMovies.has(movie.id);
            const poster =
              movie.poster ||
              movie.banner ||
              movie.posterUrl ||
              movie.posterurl ||
              "/assets/img/movie-placeholder.png";
            const cast = parseCastArray(movie.cast);

            // console.log("Movie", movie);

            return (
              <div key={movie.id} className="acc-movie-card">
                <div className="acc-movie-main">
                  <div className="acc-movie-poster">
                    <img src={poster} alt={movie.title} />
                  </div>

                  <div className="acc-movie-info">
                    <h4 className="acc-movie-title">
                      {" "}
                      {movie.title.replace(/\(.*?\)/g, "").trim()}
                    </h4>
                    <div className="acc-movie-tags">
                      {(movie.ageRating || movie.rating) && (
                        <span className="tag tag-rating">
                          {movie.ageRating || movie.rating}
                        </span>
                      )}
                      {movie.duration && (
                        <span className="tag tag-duration">
                          {formatDurationLabel(movie.duration)}
                        </span>
                      )}
                      {movie.language && (
                        <span className="tag tag-lang">{movie.language}</span>
                      )}
                      {movie.genre &&
                        String(movie.genre)
                          .replace(/[{}"]/g, "")
                          .split(",")
                          .map((g) => g.trim())
                          .filter(Boolean)
                          .map((g, i) => (
                            <span key={i} className="tag tag-genre">
                              {g}
                            </span>
                          ))}
                    </div>
                    {cast.length > 0 && (
                      <p className="acc-movie-cast">
                        <strong>Starring:</strong> {cast.slice(0, 4).join(", ")}
                      </p>
                    )}

                    <div className="acc-showtimes">
                      {movie.showtimes.map((st) => {
                        const status = getShowtimeStatus(st);
                        const isSoldOut = status === "sold-out";
                        const isDisabled = isInitializingBooking || isSoldOut;
                        return (
                          <div
                            key={st.id}
                            // className={`acc-st-item acc-st-${status} ${isInitializingBooking ? "disabled" : ""}`}
                            className={`acc-st-item acc-st-${status} ${isDisabled ? "disabled" : ""}`}
                            // onClick={() =>
                            //   !isInitializingBooking &&
                            //   handleShowtimeSelect(movie, st)
                            // }
                            onClick={(e) => {
                              if (isDisabled) {
                                e.preventDefault();
                                e.stopPropagation();
                                return;
                              }
                              handleShowtimeSelect(movie, st);
                            }}
                            // style={{
                            //   opacity: isInitializingBooking ? 0.6 : 1,
                            //   cursor: isInitializingBooking
                            //     ? "not-allowed"
                            //     : "pointer",
                            // }}
                            style={{
                              opacity: isDisabled ? 0.6 : 1,
                              cursor: isDisabled ? "not-allowed" : "pointer",
                            }}
                          >
                            <div className="acc-st-bar"></div>
                            <div className="acc-st-body">
                              <div className="acc-st-time">
                                {formatShowtimeLabel(st)}
                              </div>
                              {/* <div className="acc-st-type">{(st.screenType || "REGULAR").toUpperCase()}</div>*/}
                              <div className="acc-st-screen">
                                {st.screenName || "Screen"}
                              </div>
                              <div className="acc-st-screen-attr">
                                {parseAttributes(st.screenAttributes)
                                  ?.screenattributes || ""}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <button
                    className="acc-expand-btn"
                    onClick={() =>
                      setExpandedMovies((prev) => {
                        const s = new Set(prev);
                        s.has(movie.id) ? s.delete(movie.id) : s.add(movie.id);
                        return s;
                      })
                    }
                    aria-label={isExpanded ? "Collapse" : "Expand"}
                  >
                    {isExpanded ? "−" : "+"}
                  </button>
                </div>

                {isExpanded && (
                  <div className="acc-movie-details">
                    <div className="acc-detail-item">
                      <span>Director</span>
                      <p>{movie.director || "N/A"}</p>
                    </div>
                    <div className="acc-detail-item">
                      <span>Starring</span>
                      <p>{cast.length > 0 ? cast.join(", ") : "N/A"}</p>
                    </div>
                    <div className="acc-detail-item acc-detail-synopsis">
                      <span>Synopsis</span>
                      <p>{movie.description || "N/A"}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Modals */}
      {showQuantityModal && (
        <div
          className="cl-modal-overlay"
          onClick={() => {
            setShowQuantityModal(false);
            setPendingShowtime(null);
            setPendingMovie(null);
          }}
        >
          <div className="cl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cl-modal-header">
              <h5>Select Tickets</h5>
              <button
                onClick={() => {
                  setShowQuantityModal(false);
                  setPendingShowtime(null);
                  setPendingMovie(null);
                }}
              >
                &times;
              </button>
            </div>
            <div className="cl-modal-body">
              <p>Please select the number of tickets before proceeding.</p>
              <select
                className="cl-modal-select"
                value={quantity || ""}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
              >
                <option value="">Select Qty</option>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((q) => (
                  <option key={q} value={q}>
                    {q}
                  </option>
                ))}
              </select>
            </div>
            <div className="cl-modal-footer">
              <button
                className="cl-btn-secondary"
                onClick={() => {
                  setShowQuantityModal(false);
                  setPendingShowtime(null);
                  setPendingMovie(null);
                }}
              >
                Cancel
              </button>
              <button
                className="cl-btn-primary"
                onClick={handleQuantitySelect}
                disabled={!quantity || quantity < 1 || quantity > 10}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showErrorPopup && (
        <div
          className="cl-modal-overlay"
          onClick={() => setShowErrorPopup(false)}
        >
          <div
            className="cl-modal cl-error-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cl-error-icon">⚠</div>
            <p>{errorMessage}</p>
            <button
              className="cl-btn-primary"
              onClick={() => setShowErrorPopup(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <AgeWarningModal
        isOpen={showAgeWarning}
        onClose={() => {
          setShowAgeWarning(false);
          setPendingBookingData(null);
        }}
        onConfirm={handleAgeWarningConfirm}
      />
    </div>
  );
};

// ─── Main Listing Page ─────────────────────────────────────────────────────

const ShowTimeCinemas = () => {
  const { getLocationForAPI, formatDistance, selectedCity } = useLocation();
  const [cinemas, setCinemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCinemaId, setOpenCinemaId] = useState(null);

  useEffect(() => {
    const fetchNearby = async () => {
      try {
        setLoading(true);
        const filters = { limit: 100, sortBy: "name" };
        const loc = getLocationForAPI();
        if (loc) {
          filters.latitude = loc.lat;
          filters.longitude = loc.lng;
        }
        const res = await api.getCinemas(filters);
        const sorted = (Array.isArray(res) ? res : [])
          .filter((c) => c.distance != null && c.distance <= 25)
          .sort((a, b) => a.distance - b.distance);
        setCinemas(sorted);
        // Set the first cinema to be expanded by default
        if (sorted.length > 0) {
          setOpenCinemaId(sorted[0].id);
        }
      } catch (e) {
        console.error("Nearby cinema fetch failed", e);
        setCinemas([]);
      } finally {
        setLoading(false);
      }
    };
    fetchNearby();
  }, [selectedCity]);

  const toggleCinema = (id) =>
    setOpenCinemaId((prev) => (prev === id ? null : id));

  if (loading) {
    return (
      <div className="cl-page container">
        <div className="cl-header">
          <h2>Nearby Cinemas</h2>
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="cl-card-skeleton">
            <div className="cls-left">
              <div className="cls-name"></div>
              <div className="cls-addr"></div>
              <div className="cls-dist"></div>
            </div>
            <div className="cls-btn"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="cl-page container">
      <div className="cl-header">
        <h2>
          Nearby Cinemas{selectedCity?.name ? `  ${selectedCity.name}` : ""}
        </h2>
        <p>
          {cinemas.length} cinema{cinemas.length !== 1 ? "s" : ""} found
        </p>
      </div>

      {cinemas.length === 0 ? (
        <div className="cl-empty">
          <div className="cl-empty-icon">🎬</div>
          <h4>No showtimes found near the selected location</h4>
          <p>Try switching to a nearby location to see available shows.</p>
          <NearbyLocationsList />
        </div>
      ) : (
        <div className="cl-list">
          {cinemas.map((cinema) => {
            const isOpen = openCinemaId === cinema.id;
            return (
              <div
                key={cinema.id}
                className={`cl-cinema-item ${isOpen ? "open" : ""}`}
              >
                {/* Cinema Header Row */}
                <div
                  className="cl-cinema-header"
                  onClick={() => toggleCinema(cinema.id)}
                >
                  <div className="cl-cinema-left">
                    {/* <div className="cl-cinema-icon">🎭</div> */}
                    <div className="cl-cinema-text">
                      <h3 className="cl-cinema-name">{cinema.name}</h3>
                      <p className="cl-cinema-addr">
                        {cinema.address ||
                          cinema.location ||
                          "Address not available"}
                      </p>
                      <div className="cl-cinema-meta">
                        <span className="cl-dist">
                          📍 {formatDistance(cinema.distance)}
                        </span>
                        {cinema.screens && (
                          <span className="cl-screens">
                            🎬 {cinema.screens} Screens
                          </span>
                        )}
                        <AmenityList
                          amenities={cinema.amenities}
                          variant="inline"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="cl-cinema-right">
                    <button
                      className={`cl-toggle-btn ${isOpen ? "open" : ""}`}
                      aria-label={isOpen ? "Collapse cinema" : "Expand cinema"}
                    >
                      <span className="cl-toggle-icon">
                        {isOpen ? "−" : "+"}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Accordion Body */}
                {isOpen && (
                  <CinemaAccordion
                    cinema={cinema}
                    onClose={() => setOpenCinemaId(null)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ShowTimeCinemas;
